/**
 * Event attachments service
 *
 * Files (marketing PDFs / images) attached to an event, for sharing between event owners
 * and as an internal record.
 *
 * Storage: the pre-existing `event-attachments` bucket, which is PRIVATE. Files are read
 * through short-lived signed URLs, never public URLs. (This is the first caller of
 * `getSignedUrl` in the repo; the event *image* feature uses `getPublicUrl` against the
 * `documents` bucket and is deliberately left alone.)
 *
 * All limits are enforced server-side — the checks in this file exist only to give the
 * operator fast feedback, and are NOT the enforcement point. See
 * database/migrations/2026-09-10-009-event-file-attachments.sql for the four real layers:
 * bucket constraints, table CHECKs, the count-cap trigger, and the RPC role gate.
 */

import { supabase } from "./supabase";
import { deleteFile, getSignedUrl, uploadFile } from "./storage.service";

export const EVENT_ATTACHMENTS_BUCKET = "event-attachments";

/** Mirrors the bucket + table CHECK constraints. Server-enforced; duplicated here for UX. */
export const EVENT_ATTACHMENT_MAX_BYTES = 26214400; // 25 MiB
export const EVENT_ATTACHMENT_MAX_PER_EVENT = 20;
export const EVENT_ATTACHMENT_ALLOWED_MIME = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
] as const;

/** For the file picker's `accept` attribute. Advisory only — a picker filter is not a limit. */
export const EVENT_ATTACHMENT_ACCEPT =
  ".pdf,.png,.jpg,.jpeg,.webp,.gif,application/pdf,image/png,image/jpeg,image/webp,image/gif";

export interface EventAttachment {
  id: string;
  event_id: string;
  storage_path: string;
  file_name: string;
  mime_type: string;
  file_size_bytes: number;
  upload_status: string;
  uploaded_by: string;
  uploaded_by_name: string | null;
  uploaded_at: string;
  deleted_at: string | null;
  deleted_by_name: string | null;
}

/** Human-readable size, e.g. "1.4 MB". */
export const formatAttachmentSize = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return "0 KB";
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const isAllowedAttachmentMime = (mime: string): boolean =>
  (EVENT_ATTACHMENT_ALLOWED_MIME as readonly string[]).includes(mime);

/**
 * Client-side pre-check. Returns an error string, or null when the file looks acceptable.
 * The server re-checks all of this; skipping it would only cost the operator a round trip.
 */
export const validateAttachmentFile = (file: File): string | null => {
  if (!isAllowedAttachmentMime(file.type)) {
    return `"${file.name}" is not an allowed file type. Attach a PDF, PNG, JPEG, WebP or GIF.`;
  }
  if (file.size <= 0) {
    return `"${file.name}" is empty.`;
  }
  if (file.size > EVENT_ATTACHMENT_MAX_BYTES) {
    return `"${file.name}" is ${formatAttachmentSize(file.size)}, over the 25 MB limit.`;
  }
  return null;
};

/** Lists an event's live attachments, newest first, with uploader names resolved. */
export const listEventAttachments = async (
  eventId: string,
): Promise<EventAttachment[]> => {
  const { data, error } = await supabase.rpc("list_event_attachments", {
    p_event_id: eventId,
    p_include_deleted: false,
  });
  if (error) {
    throw error;
  }
  return (data ?? []) as EventAttachment[];
};

/**
 * Uploads one file in three steps:
 *   1. `create_event_attachment` reserves a row and returns the storage path it owns.
 *      This is where the role gate, MIME/size caps and per-event count cap are applied.
 *   2. The bytes go to that exact path (the bucket re-enforces size and MIME).
 *   3. `confirm_event_attachment` proves the object landed and re-validates the REAL
 *      stored size and MIME, so a client that lied in step 1 still gets rejected.
 *
 * If step 2 or 3 fails, the reserved row is rolled back so it cannot hold a slot against
 * the count cap or linger as a half-made attachment.
 */
export const uploadEventAttachment = async (
  eventId: string,
  file: File,
): Promise<EventAttachment> => {
  const localError = validateAttachmentFile(file);
  if (localError) {
    throw new Error(localError);
  }

  const { data: reserved, error: reserveError } = await supabase.rpc(
    "create_event_attachment",
    {
      p_event_id: eventId,
      p_file_name: file.name,
      p_mime_type: file.type,
      p_file_size_bytes: file.size,
    },
  );
  if (reserveError) {
    throw reserveError;
  }

  const attachmentId = (reserved as { id: string } | null)?.id;
  const storagePath = (reserved as { storage_path: string } | null)
    ?.storage_path;
  if (!attachmentId || !storagePath) {
    throw new Error("Could not reserve an attachment slot for this event.");
  }

  try {
    await uploadFile({
      provider: "supabase",
      bucket: EVENT_ATTACHMENTS_BUCKET,
      path: storagePath,
      file,
      contentType: file.type,
      upsert: false,
    });
  } catch (uploadError) {
    await rollbackReservation(attachmentId, storagePath);
    throw uploadError;
  }

  const confirmed: { file_size_bytes?: number; mime_type?: string } = {};
  try {
    const { data: confirmData, error: confirmError } = await supabase.rpc(
      "confirm_event_attachment",
      { p_attachment_id: attachmentId },
    );
    if (confirmError) {
      throw confirmError;
    }
    Object.assign(confirmed, (confirmData as typeof confirmed | null) ?? {});
  } catch (confirmError) {
    await rollbackReservation(attachmentId, storagePath);
    throw confirmError;
  }

  // The upload is CONFIRMED. The read-back below only enriches the record (uploader name etc.); if it
  // fails, return what is already known rather than throwing -- throwing here lost the attachment id,
  // so a caller (the CTA flyer) could neither finalize nor cancel it and a ready, unlinked copy
  // lingered with only "Failed to fetch" (round-4 flyer audit).
  try {
    const refreshed = await listEventAttachments(eventId);
    const created = refreshed.find((a) => a.id === attachmentId);
    if (created) {
      return created;
    }
  } catch (readErr) {
    console.warn("Attachment confirmed; read-back failed:", readErr);
  }
  return {
    id: attachmentId,
    event_id: eventId,
    storage_path: storagePath,
    file_name: file.name,
    mime_type: confirmed.mime_type ?? file.type,
    file_size_bytes: confirmed.file_size_bytes ?? file.size,
    upload_status: "ready",
    uploaded_by: "",
    uploaded_by_name: null,
    uploaded_at: new Date().toISOString(),
    deleted_at: null,
    deleted_by_name: null,
  };
};

/**
 * Best-effort cleanup of a reservation whose upload did not complete. Deliberately
 * swallows its own errors: the caller is already throwing the real failure, and the
 * reconciliation function `find_orphaned_event_attachment_objects()` will surface
 * anything left behind.
 */
const rollbackReservation = async (
  attachmentId: string,
  storagePath: string,
): Promise<void> => {
  try {
    await supabase.rpc("soft_delete_event_attachment", {
      p_attachment_id: attachmentId,
    });
  } catch {
    /* reported by reconciliation */
  }
  try {
    await deleteFile("supabase", EVENT_ATTACHMENTS_BUCKET, [storagePath]);
  } catch {
    /* reported by reconciliation */
  }
};

/**
 * Short-lived signed URL for viewing or downloading. The bucket is private, so this is
 * the only way to read a file, and the link expires in 5 minutes.
 */
export const getEventAttachmentUrl = async (
  storagePath: string,
): Promise<string> => {
  return getSignedUrl({
    provider: "supabase",
    bucket: EVENT_ATTACHMENTS_BUCKET,
    path: storagePath,
    expiresIn: 300,
  });
};

/**
 * Short-lived signed URL that DOWNLOADS the file under its original name. Storage answers it with
 * `Content-Disposition: attachment`, which is what makes the browser save it: the file lives on the
 * storage domain, so an <a download> attribute on a cross-origin link is ignored and the browser just
 * opens images and PDFs in a tab (reported 2026-09-29 for the "Scan to join" flyer).
 */
export const getEventAttachmentDownloadUrl = async (
  storagePath: string,
  fileName: string,
): Promise<string> => {
  const { data, error } = await supabase.storage
    .from(EVENT_ATTACHMENTS_BUCKET)
    .createSignedUrl(storagePath, 300, { download: fileName || true });
  if (error || !data?.signedUrl) {
    throw new Error(error?.message ?? "Could not prepare the download");
  }
  return data.signedUrl;
};

/**
 * Removes an attachment. The row is SOFT-deleted (the tombstone records what was attached
 * and who removed it, for internal audit) and the storage object is HARD-deleted, so no
 * bytes are retained. The RPC runs first so the removal is recorded even if the object
 * delete then fails; that case shows up in `find_orphaned_event_attachment_objects()`.
 */
export const deleteEventAttachment = async (
  attachmentId: string,
): Promise<void> => {
  const { data, error } = await supabase.rpc("soft_delete_event_attachment", {
    p_attachment_id: attachmentId,
  });
  if (error) {
    throw error;
  }

  const storagePath = (data as { storage_path: string } | null)?.storage_path;
  if (storagePath) {
    await deleteFile("supabase", EVENT_ATTACHMENTS_BUCKET, [storagePath]);
  }
};
